import React, {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import {
  Input,
  Select,
  Row,
  Col,
  Button,
  Space,
  Form,
  Spin,
  message,
  Modal,
  Image,
  Card,
  Tag,
  Empty,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";

import { Locale, ApiManager } from "../../utils";
import { connect } from "react-redux";
import { getUserInfo } from "../../utils/redux/reducers/User";
import TranslateBtn from "../../components/product/TranslateBtn";
import {
  hasPropertyName,
  buildSkuFormValues,
  snapshotSkuValuesByLabelFromGroups,
  snapshotSkuValuesFromApiGroups,
  mapGroupFromApi,
  extractCreatedPropertyId,
  extractCreatedGroupId,
  buildMatrixForDisplay,
  syncSkuFormFields,
  resolveGroupsForProductSpecs,
  getDisplayGroupsForProduct,
  groupsHaveProperties,
  normalizeGroupsFromApi,
  ensurePersistableGroups,
  toDraftGroupsFromApi,
} from "./productSpecHelpers";

import "./productPropertyForm.scss";
import ImageUpload from "../../components/media/ImageUpload";
import PropertyTable from "./PropertyTable";

const i18n = Locale.getInstance();
const api = ApiManager.getInstance();
const { Option } = Select;

function createDraftGroup() {
  return {
    id: null,
    zh_name: "",
    ug_name: "",
    childList: [],
    index: 0,
    isDraft: true,
  };
}

function createDraftProperty() {
  return {
    id: null,
    zh_name: "",
    ug_name: "",
    order: 0,
    image: null,
    isDraft: true,
  };
}

function ProductPropertyFormInner({ user, product, parentForm, cloneMode = false }, ref) {
  // Clone must behave like create: never write specs back onto the source product id.
  const isDraftMode = !product?.id || !!cloneMode;
  const [groupList, setGroupList] = useState([]);
  const [skuList, setSkuList] = useState([]);
  const [groups, setGroups] = useState(
    isDraftMode && !cloneMode ? [createDraftGroup()] : []
  );
  const [openPropertyModal, setOpenPropertyModal] = useState(false);
  const [openGroupModal, setOpenGroupModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(false);
  const [groupForm] = Form.useForm();
  const [propertyForm] = Form.useForm();
  const groupsRef = useRef(groups);
  groupsRef.current = groups;

  const loadProductSpecs = useCallback(async (productIdOverride = null) => {
    const { token } = user;
    const targetProductId = productIdOverride ?? product?.id;
    if (!token || !targetProductId) return;
    // Draft create (no product yet) has nothing to load.
    if (!product?.id && !productIdOverride) return;
    // Clone draft: only load via loadCloneSpecsFromSource.
    if (cloneMode && !productIdOverride) return;

    const isZh = !i18n.isLocaleRTL();
    let groupsRes = null;
    let skuRes = null;

    try {
      groupsRes = await api.productProperty.list.run({
        token,
        query: { product_id: targetProductId },
      });
      skuRes = await api.productProperty.skuList.run({
        token,
        query: { product_id: targetProductId },
      });
    } catch (error) {
      console.log("loadProductSpecs -> error", error);
      return;
    }

    let groupsWithProperties = [];
    if (groupsRes?.result && Array.isArray(groupsRes.data)) {
      setGroupList(groupsRes.data);
      groupsWithProperties = normalizeGroupsFromApi(groupsRes.data);
    }

    const skuData = skuRes?.result ? skuRes.data || [] : [];
    if (skuRes?.result) {
      setSkuList(skuData);
    }

    if (groupsWithProperties.length === 0 && skuData.length > 0) {
      groupsWithProperties = resolveGroupsForProductSpecs([], skuData, isZh);
    }

    setGroups(groupsWithProperties);

    if (parentForm?.current && (groupsWithProperties.length > 0 || skuData.length > 0)) {
      queueMicrotask(() => {
        syncSkuFormFields(
          parentForm,
          getDisplayGroupsForProduct(groupsWithProperties, skuData, isZh),
          skuData,
          isZh
        );
      });
    }
  }, [product, user, cloneMode, parentForm]);

  const loadCloneSpecsFromSource = useCallback(async () => {
    const { token } = user;
    const sourceProductId = product?.id;
    if (!token || !sourceProductId || !cloneMode) return;

    setLoading(true);
    try {
      const groupsRes = await api.productProperty.list.run({
        token,
        query: { product_id: sourceProductId },
      });
      const skuRes = await api.productProperty.skuList.run({
        token,
        query: { product_id: sourceProductId },
      });

      const isZh = !i18n.isLocaleRTL();
      let apiGroups = [];
      if (groupsRes?.result && Array.isArray(groupsRes.data)) {
        setGroupList(groupsRes.data);
        apiGroups = normalizeGroupsFromApi(groupsRes.data);
      }
      const skuData = skuRes?.result ? skuRes.data || [] : [];
      if (apiGroups.length === 0 && skuData.length > 0) {
        apiGroups = resolveGroupsForProductSpecs([], skuData, isZh);
      }

      const priceSnapshot = snapshotSkuValuesFromApiGroups(apiGroups, skuData, isZh);
      const draftGroups = toDraftGroupsFromApi(apiGroups);
      setGroups(
        draftGroups.length > 0 ? draftGroups : [createDraftGroup()]
      );
      // New product has no SKU rows yet — matrix uses draft groups + snapshot prices.
      setSkuList([]);

      if (parentForm?.current && draftGroups.length > 0) {
        queueMicrotask(() => {
          syncSkuFormFields(parentForm, draftGroups, [], isZh, priceSnapshot);
        });
      }
    } catch (error) {
      console.log("loadCloneSpecsFromSource -> error", error);
    } finally {
      setLoading(false);
    }
  }, [user, product, cloneMode, parentForm]);

  useEffect(() => {
    if (cloneMode) {
      loadCloneSpecsFromSource();
      return;
    }
    if (isDraftMode) return;
    loadProductSpecs();
  }, [loadProductSpecs, loadCloneSpecsFromSource, isDraftMode, cloneMode]);

  useImperativeHandle(ref, () => ({
    getGroups: () => groups,
    getSkuList: () => skuList,
    getSkuSnapshotByLabel: () => {
      const formValues = parentForm?.current?.getFieldsValue(true) || {};
      return snapshotSkuValuesByLabelFromGroups(
        groups,
        formValues,
        !i18n.isLocaleRTL()
      );
    },
    rebuildSkuFormFields: async (productId, valueSnapshot = {}) => {
      const { token } = user;
      if (!token || !productId) return null;

      const groupsRes = await api.productProperty.list.run({
        token,
        query: { product_id: productId },
      });
      const skuRes = await api.productProperty.skuList.run({
        token,
        query: { product_id: productId },
      });

      if (!groupsRes.result) return null;

      const isZh = !i18n.isLocaleRTL();
      let groupsWithProperties = Array.isArray(groupsRes.data)
        ? normalizeGroupsFromApi(groupsRes.data)
        : [];
      const skuData = skuRes.result ? skuRes.data || [] : [];

      if (groupsWithProperties.length === 0 && skuData.length > 0) {
        groupsWithProperties = resolveGroupsForProductSpecs([], skuData, isZh);
      }

      setGroupList(groupsRes.data);
      setGroups(groupsWithProperties);
      groupsRef.current = groupsWithProperties;
      if (skuRes.result) {
        setSkuList(skuData);
      }

      const formValues = buildSkuFormValues(
        groupsWithProperties,
        skuData,
        isZh,
        valueSnapshot
      );

      if (parentForm?.current) {
        parentForm.current.setFieldsValue(formValues);
      }

      // Return groups/skuList so callers can build payloads before React re-renders.
      return { formValues, groups: groupsWithProperties, skuList: skuData, isZh };
    },
    validateSpecs: () => {
      const isZh = !i18n.isLocaleRTL();
      const validGroups = groups.filter(
        (g) =>
          (g.zh_name?.trim() || g.ug_name?.trim() || g.childList?.length) &&
          g.childList?.some(hasPropertyName)
      );
      if (validGroups.length === 0) {
        return { valid: false, message: i18n.t("property.selectGroup") };
      }

      const matrix = buildMatrixForDisplay(groups, skuList, isZh);
      if (matrix.length === 0) {
        return { valid: false, message: i18n.t("property.addChild") };
      }

      const formValues = parentForm?.current?.getFieldsValue(true) || {};
      const prices = formValues.property_price || {};
      const quantities = formValues.property_quantity || {};
      for (const row of matrix) {
        const code = row.code;
        if (!prices[code] || String(prices[code]).trim() === "") {
          return { valid: false, message: i18n.t("property.priceWarning") };
        }
        if (!quantities[code] || String(quantities[code]).trim() === "") {
          return { valid: false, message: i18n.t("property.quantityWarning") };
        }
      }
      return { valid: true };
    },
    persistSpecEntities: async (productId) => {
      const { token } = user;
      if (!token) throw new Error(i18n.t("base.api.errUnknown"));
      const isZh = !i18n.isLocaleRTL();
      const formValues = parentForm?.current?.getFieldsValue(true) || {};
      const groupsToPersist = ensurePersistableGroups(
        groupsRef.current,
        formValues,
        isZh
      );

      if (
        !groupsToPersist.some((group) =>
          group.childList?.some(hasPropertyName)
        )
      ) {
        throw new Error(i18n.t("property.selectGroup"));
      }

      for (let groupIndex = 0; groupIndex < groupsToPersist.length; groupIndex++) {
        const group = groupsToPersist[groupIndex];
        const defaultName = isZh
          ? `规格${groupIndex + 1}`
          : `ئۆلچەم ${groupIndex + 1}`;
        const groupZh =
          group.zh_name?.trim() ||
          group.zhController?.text?.trim?.() ||
          "";
        const groupUg =
          group.ug_name?.trim() ||
          group.ugController?.text?.trim?.() ||
          "";
        const resolvedGroupZh = groupZh || group.zh_name || defaultName;
        const resolvedGroupUg = groupUg || group.ug_name || defaultName;
        if (!group.childList?.some(hasPropertyName)) continue;

        let groupId = group.id;
        if (!groupId) {
          const res = await api.productProperty.add.run({
            token,
            data: {
              zh_name: resolvedGroupZh,
              ug_name: resolvedGroupUg,
              product_id: productId,
            },
          });
          const createdGroupId = extractCreatedGroupId(res);
          if (!res.result || !createdGroupId) {
            throw new Error(res.message || i18n.t("base.api.errUnknown"));
          }
          groupId = createdGroupId;
          group.id = groupId;
        }

        for (let i = 0; i < group.childList.length; i++) {
          const prop = group.childList[i];
          if (!hasPropertyName(prop)) continue;
          if (prop.id) continue;

          const zh = prop.zh_name?.trim() || "";
          const ug = prop.ug_name?.trim() || "";
          const apiConfig = {
            token,
            data: {
              zh_name: zh,
              ug_name: ug,
              order: prop.order || i,
              property_group_id: groupId,
              product_id: productId,
            },
          };

          let imagePath = prop.image;
          if (prop.image && Array.isArray(prop.image) && prop.image.length > 0) {
            imagePath = prop.image[0]?.file_path || prop.image[0]?.url;
          }
          if (imagePath && typeof imagePath === "string") {
            apiConfig.data.image = imagePath;
          }

          const res = await api.productProperty.addChild.run(apiConfig);
          if (!res.result) {
            throw new Error(res.message || i18n.t("base.api.errUnknown"));
          }
          const createdId = extractCreatedPropertyId(res);
          if (createdId) {
            prop.id = createdId;
          }
        }
      }

      setGroups(groupsToPersist);
      groupsRef.current = groupsToPersist;
      return groupsToPersist;
    },
    refreshSkuList: (productIdOverride = null) =>
      productIdOverride || (!cloneMode && product?.id)
        ? loadProductSpecs(productIdOverride || product.id)
        : null,
  }), [groups, skuList, user, isDraftMode, cloneMode, parentForm, loadProductSpecs, product]);

  const refreshGroups = async () => {
    await loadProductSpecs();
  };

  function onDeleteGroup(group) {
    if (!group?.id) {
      setGroups((prevGroups) => prevGroups.filter((g) => g !== group));
      return;
    }

    Modal.confirm({
      title: i18n.t("sales.deleteWarning"),
      content: i18n.t("property.deleteGroup"),
      okText: i18n.t("base.modal.okText"),
      cancelText: i18n.t("base.modal.cancelText"),
      onOk: async () => {
        const { token } = user;
        if (token) {
          const deleteApi = api.productProperty.delete;
          const apiConfig = {
            token,
            path: { id: group.id },
          };
          try {
            const res = await deleteApi.run(apiConfig);
            if (res.result) {
              message.success(res.message);
              await refreshGroups();
            } else {
              message.error(res.message);
            }
          } catch (error) {
            console.log("onDeleteGroup -> error", error);
            message.error(i18n.t("base.api.errUnknown"));
          }
        }
      },
    });
  }

  async function onAddGroup(values) {
    const { zh_name, ug_name } = values;

    if (isDraftMode) {
      const newGroup = {
        id: null,
        zh_name,
        ug_name,
        childList: [],
        index: groups.length,
        isDraft: true,
      };
      setGroups([...groups, newGroup]);
      groupForm.resetFields();
      setOpenGroupModal(false);
      return;
    }

    const { token } = user;
    if (token) {
      const adApi = api.productProperty.add;
      setLoading(true);
      try {
        const apiConfig = {
          token,
          data: {
            zh_name,
            ug_name,
            product_id: product.id,
          },
        };
        const res = await adApi.run(apiConfig);
        if (res.result) {
          message.success(
            res.message ||
              i18n.t("property.addGroup") +
                " " +
                i18n.t("base.modal.successTitle")
          );
          groupForm.resetFields();
          setOpenGroupModal(false);
          await refreshGroups();
        } else {
          message.error(res.message);
        }
      } catch (error) {
        console.log("onAddGroup -> error", error);
        message.error(i18n.t("base.api.errUnknown"));
      } finally {
        setLoading(false);
      }
    }
  }

  async function onAddNewProperty(values) {
    const { zh_name, ug_name, order } = values;

    if (isDraftMode && selectedGroup) {
      const newProp = {
        id: null,
        zh_name,
        ug_name,
        order: order || 0,
        image:
          values.image && values.image.length > 0
            ? values.image[0]?.file_path || values.image[0]?.url
            : null,
        isDraft: true,
      };
      selectedGroup.childList = [...(selectedGroup.childList || []), newProp];
      setGroups([...groups]);
      propertyForm.resetFields();
      setOpenPropertyModal(false);
      setSelectedProperty(null);
      return;
    }

    const { token } = user;
    if (token && selectedGroup) {
      const propertyApi = api.productProperty.addChild;
      setLoading(true);
      try {
        const apiConfig = {
          token,
          data: {
            zh_name,
            ug_name,
            order: order || 0,
            property_group_id: selectedGroup.id,
            product_id: product.id,
          },
        };
        if (values.image && values.image.length > 0) {
          apiConfig.data.image = values.image[0]["file_path"];
        }
        const res = await propertyApi.run(apiConfig);
        if (res.result) {
          message.success(
            res.message ||
              i18n.t("property.addChild") +
                " " +
                i18n.t("base.modal.successTitle")
          );
          propertyForm.resetFields();
          setOpenPropertyModal(false);
          setSelectedProperty(null);
          await refreshGroups();
        } else {
          message.error(res.message);
        }
      } catch (error) {
        console.log("onAddNewProperty -> error", error);
        message.error(i18n.t("base.api.errUnknown"));
      } finally {
        setLoading(false);
      }
    }
  }

  async function onUpdateProperty(values) {
    const { token } = user;
    if (token && selectedProperty && selectedGroup) {
      const propertyApi = api.productProperty.updateChild;
      setLoading(true);
      try {
        const { zh_name, ug_name, order } = values;
        const apiConfig = {
          token,
          path: { id: selectedProperty.id },
          data: {
            zh_name,
            ug_name,
            property_group_id: selectedGroup.id,
            product_id: product.id,
            order: order || 0,
          },
        };
        if (values.image && values.image.length > 0) {
          apiConfig.data.image = values.image[0]["file_path"];
        }
        const res = await propertyApi.run(apiConfig);
        if (res.result) {
          message.success(res.message || i18n.t("base.modal.successTitle"));
          propertyForm.resetFields();
          setOpenPropertyModal(false);
          setSelectedProperty(null);
          await refreshGroups();
        } else {
          message.error(res.message);
        }
      } catch (error) {
        console.log("onUpdateProperty -> error", error);
        message.error(i18n.t("base.api.errUnknown"));
      } finally {
        setLoading(false);
      }
    }
  }

  function onSelectGroup(index, value) {
    const selectedGroupData = groupList.find((g) => g.id === value);
    if (selectedGroupData) {
      const updatedGroups = [...groups];
      updatedGroups[index] = mapGroupFromApi(selectedGroupData, index);
      setGroups(updatedGroups);
    }
  }

  function onDeleteProperty(id, group, property) {
    if (isDraftMode && property?.isDraft) {
      group.childList = group.childList.filter((p) => p !== property);
      setGroups([...groups]);
      return;
    }

    Modal.confirm({
      title: i18n.t("sales.deleteWarning"),
      okText: i18n.t("base.modal.okText"),
      cancelText: i18n.t("base.modal.cancelText"),
      onOk: async () => {
        const { token } = user;
        if (token) {
          const deleteApi = api.productProperty.deleteChild;
          const apiConfig = {
            token,
            path: { id },
          };
          try {
            const res = await deleteApi.run(apiConfig);
            if (res.result) {
              message.success(res.message);
              await refreshGroups();
            } else {
              message.error(res.message);
            }
          } catch (error) {
            console.log("onDeleteProperty -> error", error);
            message.error(i18n.t("base.api.errUnknown"));
          }
        }
      },
    });
  }

  function onEditProperty(property, group) {
    setSelectedGroup(group);
    setSelectedProperty(property);
    setOpenPropertyModal(true);
    propertyForm.setFieldsValue({
      zh_name: property.zh_name,
      ug_name: property.ug_name,
      order: property.order,
      image:
        property.image && property.image.length > 0 ? property.image : null,
    });
  }

  function onAddProperty(group) {
    setSelectedGroup(group);
    setSelectedProperty(null);
    setOpenPropertyModal(true);
    propertyForm.resetFields();
  }

  function onDraftGroupFieldChange(group, field, value) {
    group[field] = value;
    setGroups([...groups]);
  }

  const availableGroups = groupList.filter(
    (g) => !groups.some((selectedGroup) => selectedGroup.id === g.id)
  );

  const isZh = !i18n.isLocaleRTL();
  const displayGroups = getDisplayGroupsForProduct(groups, skuList, isZh);

  return (
    <div className="product-property-form">
      <div className="property-groups-section">
        <div className="section-header">
          {displayGroups.length < 2 && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                if (isDraftMode) {
                  setGroups([...groups, createDraftGroup()]);
                } else {
                  setOpenGroupModal(true);
                }
              }}
            >
              {i18n.t("property.addGroup")}
            </Button>
          )}
        </div>

        {displayGroups.length === 0 ? (
          <Empty
            description={i18n.t("property.selectGroup")}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Row gutter={[16, 16]}>
            {displayGroups.map((group, index) => (
              <Col span={24} key={group.id || `new-${index}`}>
                <Card
                  className="property-group-card"
                  title={
                    <div className="card-title">
                      <span>
                        {isDraftMode && !group.id
                          ? i18n.t("property.newGroup")
                          : i18n.getPropNameByLocale(group, "name")}
                      </span>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => onDeleteGroup(group)}
                        size="small"
                      />
                    </div>
                  }
                  extra={
                    isDraftMode && !group.id ? (
                      <Space direction="vertical" style={{ width: 220 }}>
                        <Input
                          placeholder={i18n.t("property.groupZh")}
                          value={group.zh_name || ""}
                          onChange={(e) =>
                            onDraftGroupFieldChange(
                              group,
                              "zh_name",
                              e.target.value
                            )
                          }
                        />
                        <Input
                          placeholder={i18n.t("property.groupUg")}
                          value={group.ug_name || ""}
                          onChange={(e) =>
                            onDraftGroupFieldChange(
                              group,
                              "ug_name",
                              e.target.value
                            )
                          }
                        />
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          size="small"
                          onClick={() => onAddProperty(group)}
                        >
                          {i18n.t("property.addChild")}
                        </Button>
                      </Space>
                    ) : group.id || isDraftMode || group.isDraft ? (
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        size="small"
                        onClick={() => onAddProperty(group)}
                      >
                        {i18n.t("property.addChild")}
                      </Button>
                    ) : (
                      <Select
                        style={{ width: 200 }}
                        placeholder={i18n.t("property.selectGroup")}
                        onChange={(value) => onSelectGroup(index, value)}
                        value={group.id}
                      >
                        {availableGroups.map((g) => (
                          <Option key={g.id} value={g.id}>
                            {i18n.getPropNameByLocale(g, "name")}
                          </Option>
                        ))}
                      </Select>
                    )
                  }
                >
                  <Spin spinning={loading}>
                    {group.childList && group.childList.length > 0 ? (
                      <div className="properties-list">
                        {group.childList.map((property) => (
                          <div key={property.id || property.zh_name} className="property-item">
                            <div className="property-content">
                              {property.image && (
                                <Image
                                  width={40}
                                  height={40}
                                  src={property.image}
                                  preview={false}
                                  style={{ borderRadius: 4, marginRight: 8 }}
                                />
                              )}
                              <Tag color="blue" className="property-tag">
                                {i18n.getPropNameByLocale(property, "name")}
                              </Tag>
                            </div>
                            <Space>
                              {!isDraftMode && property.id && (
                                <Button
                                  type="text"
                                  icon={<EditOutlined />}
                                  size="small"
                                  onClick={() =>
                                    onEditProperty(property, group)
                                  }
                                />
                              )}
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                                onClick={() =>
                                  onDeleteProperty(
                                    property.id,
                                    group,
                                    property
                                  )
                                }
                              />
                            </Space>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Empty
                        description={i18n.t("property.addChild")}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        style={{ padding: "20px 0" }}
                      />
                    )}
                  </Spin>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        {groups.length === 1 && displayGroups.length < 2 && availableGroups.length > 0 && !isDraftMode && (
          <div style={{ marginTop: 16 }}>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              block
              onClick={() => {
                const newGroup = { childList: [], index: groups.length };
                setGroups([...groups, newGroup]);
              }}
            >
              {i18n.t("property.newGroup")}
            </Button>
          </div>
        )}
      </div>

      {(displayGroups.length > 0 || skuList.length > 0) && (
        <PropertyTable
          groups={displayGroups}
          skuList={skuList}
          parentForm={parentForm}
        />
      )}

      <Modal
        title={i18n.t("property.addGroup")}
        open={openGroupModal}
        onCancel={() => {
          setOpenGroupModal(false);
          groupForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={groupForm} layout="vertical" onFinish={onAddGroup}>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => (
              <Form.Item
                name="zh_name"
                label={i18n.t("property.groupZh")}
                rules={[
                  {
                    required: true,
                    message: i18n.t("sales.zhTitleWarning"),
                  },
                ]}
              >
                <Input
                  placeholder={i18n.t("property.groupZh")}
                  addonAfter={
                    <TranslateBtn
                      context={getFieldValue("zh_name")}
                      onFinish={(value) =>
                        setFieldsValue({
                          ug_name: value,
                        })
                      }
                      from="zh"
                      to="ug"
                    />
                  }
                />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => (
              <Form.Item
                name="ug_name"
                label={i18n.t("property.groupUg")}
                rules={[
                  {
                    required: true,
                    message: i18n.t("sales.ugTitleWarning"),
                  },
                ]}
              >
                <Input
                  placeholder={i18n.t("property.groupUg")}
                  addonAfter={
                    <TranslateBtn
                      context={getFieldValue("ug_name")}
                      onFinish={(value) =>
                        setFieldsValue({
                          zh_name: value,
                        })
                      }
                      from="ug"
                      to="zh"
                    />
                  }
                />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={loading}
              >
                {i18n.t("property.submit")}
              </Button>
              <Button
                onClick={() => {
                  setOpenGroupModal(false);
                  groupForm.resetFields();
                }}
              >
                {i18n.t("base.modal.cancelText")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={selectedProperty ? "编辑规格" : i18n.t("property.addChild")}
        open={openPropertyModal}
        onCancel={() => {
          setOpenPropertyModal(false);
          propertyForm.resetFields();
          setSelectedProperty(null);
        }}
        onOk={() => {
          propertyForm.submit();
        }}
        okText={i18n.t("base.modal.okText")}
        cancelText={i18n.t("base.modal.cancelText")}
        width={500}
      >
        <Form
          form={propertyForm}
          layout="vertical"
          onFinish={(values) => {
            if (selectedProperty && !selectedProperty.isDraft) {
              onUpdateProperty(values);
            } else {
              onAddNewProperty(values);
            }
          }}
        >
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => (
              <Form.Item
                name="zh_name"
                label={i18n.t("property.childZh")}
                rules={[
                  {
                    required: true,
                    message: i18n.t("sales.zhTitleWarning"),
                  },
                ]}
              >
                <Input
                  placeholder={i18n.t("property.childZh")}
                  addonAfter={
                    <TranslateBtn
                      context={getFieldValue("zh_name")}
                      onFinish={(value) =>
                        setFieldsValue({
                          ug_name: value,
                        })
                      }
                      from="zh"
                      to="ug"
                    />
                  }
                />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => (
              <Form.Item
                name="ug_name"
                label={i18n.t("property.childUg")}
                rules={[
                  {
                    required: true,
                    message: i18n.t("sales.ugTitleWarning"),
                  },
                ]}
              >
                <Input
                  placeholder={i18n.t("property.childUg")}
                  addonAfter={
                    <TranslateBtn
                      context={getFieldValue("ug_name")}
                      onFinish={(value) =>
                        setFieldsValue({
                          zh_name: value,
                        })
                      }
                      from="ug"
                      to="zh"
                    />
                  }
                />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item
            name="order"
            label={i18n.t("common.orderDescription")}
          >
            <Input placeholder={i18n.t("common.orderDescription")} />
          </Form.Item>
          {selectedGroup?.index === 0 && (
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => {
                const images = getFieldValue("image")
                  ? [getFieldValue("image")]
                  : [];
                return (
                  <Form.Item name="image" label="图片">
                    <ImageUpload
                      setSelectedImages={(image) => {
                        setFieldsValue({
                          image,
                        });
                      }}
                      images={images}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}

const ProductPropertyForm = connect(
  (state) => ({ user: getUserInfo(state) }),
  null,
  null,
  { forwardRef: true }
)(forwardRef(ProductPropertyFormInner));

export default ProductPropertyForm;
